import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import Head from 'next/head';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useDelayedRender from 'shared/hooks/use-delayed-render';
import type { Dispensaries } from 'src/gql/graphql';
import useUI from 'src/hooks/use-ui';
import { useIsAccountEnabled } from 'src/hooks/use-is-account-enabled';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';

import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import StoreFrontHeader from 'src/dispensary/core-menu/components/core-menu-header/store-front-header';
import { Navigation } from 'src/dispensary/core-menu/components/header/navigation';
import { PlaceholderHeader } from 'src/dispensary/core-menu/components/header/placeholder-header';
import { PlaceholderNavigation } from 'src/dispensary/core-menu/components/header/placeholder-navigation';
import useExtractedMeta from 'src/theme/provider/commerce-components/use-extracted-meta';
import { hasStructuredData, serializeJsonLd } from 'src/components/meta-tags/json-ld/has-structured-data';
import { SkipLink } from './skip-link';

type HeaderProps = {
  dispensary?: Dispensaries | null;
};

// Delay showing placeholders to avoid flash when data loads quickly
const PLACEHOLDER_DELAY_MS = 200;

export const Header = ({ dispensary }: HeaderProps): JSX.Element | null => {
  const flags = useFlags();
  const UI = useUI();
  const delayedRender = useDelayedRender(PLACEHOLDER_DELAY_MS);
  const isAccountEnabled = useIsAccountEnabled();
  const isStoreFrontOrKiosk = UI.isStoreFront || UI.isKiosk;

  // `metaFields` is computed once at the ThemeProvider level and shared via CommerceComponentsContext
  // with CanonicalTag, DispensaryTags, ProductTags, and JsonLDScript. This avoids invoking the remote
  // theme's getStoreFrontMetaFields function more than once per navigation.
  const { components, metaFields } = useCommerceComponents();
  const ResolvedNavigation = useRemoteComponent(RemoteComponentSlot.Navigation, { fallback: Navigation });

  const derivedMetaFieldsEnabled: boolean = flags['growth.ecomm.get-store-front-meta-fields-function.rollout'] ?? false;
  // Extract meta components early to avoid conditional hook calls
  const ResolvedMeta = useMemo(() => components.StoreFrontMeta({ fallback: () => <></> }), [components]);
  const extractedMeta = useExtractedMeta(ResolvedMeta, derivedMetaFieldsEnabled);

  const titleWithFallback = metaFields?.title?.trim() ? metaFields.title : dispensary?.name ?? '';

  if (!dispensary) {
    return delayedRender(() => (
      <Wrapper $isSticky={UI.isKiosk} data-testid='dispensary-header'>
        <SkipLink />
        {UI.isKiosk || UI.isStoreFront ? <PlaceholderHeader /> : null}
        <PlaceholderNavigation />
      </Wrapper>
    ));
  }

  // Whitespace-only ogImage counts as absent so host fallback images stay active.
  const typedOgImage = metaFields?.ogImage?.trim() || undefined;

  return (
    <Wrapper $isSticky={UI.isKiosk} data-testid='dispensary-header'>
      <SkipLink />
      {extractedMeta.length > 0 && <Head>{extractedMeta}</Head>}
      {metaFields && (
        <Head>
          {/* Always render title tag so Next.js can update it */}
          <title key='title'>{titleWithFallback}</title>

          {metaFields.description && <meta name='description' content={metaFields.description} />}

          {typedOgImage && (
            <>
              <meta property='og:image' content={typedOgImage} />
              <meta name='twitter:image' content={typedOgImage} />
            </>
          )}

          {/*
            NOTE: rel="canonical" is intentionally NOT rendered here. CanonicalTag
            (src/components/meta-tags/canonical-tag.tsx) is the single owner of the canonical
            link tag across the whole app. It reads this same metaFields value, validates that
            metaFields.canonical is a well-formed absolute http(s) URL, and only then prefers it
            over the core-computed canonical. Rendering a second <link rel="canonical"> tag here
            previously caused two conflicting canonical tags to reach the DOM on custom-domain
            storefronts (ECOMM-14263).
          */}

          {hasStructuredData(metaFields.structuredData) && (
            <script
              type='application/ld+json'
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: serializeJsonLd(metaFields.structuredData),
              }}
            />
          )}

          {metaFields.customMeta?.map((meta, index) => {
            // The typed ogImage already renders og:image and twitter:image above; skip duplicates
            const customName = (meta.property ?? meta.name ?? '').toLowerCase();
            const isImageTag = customName === 'og:image' || customName === 'twitter:image';
            if (isImageTag && (typedOgImage || !meta.content?.trim())) {
              return null;
            }

            const key = meta.name ?? meta.property ?? `custom-meta-${String(index)}`;

            if (meta.name) {
              return <meta key={key} name={meta.name} content={meta.content} />;
            }

            if (meta.property) {
              return <meta key={key} property={meta.property} content={meta.content} />;
            }

            return null;
          })}
        </Head>
      )}

      {isStoreFrontOrKiosk ? (
        <>
          <StoreFrontHeader dispensary={dispensary} />
          <ResolvedNavigation dispensary={dispensary} isAccountEnabled={isAccountEnabled} />
        </>
      ) : (
        <Navigation dispensary={dispensary} isAccountEnabled={isAccountEnabled} />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.header<{ $isSticky: boolean }>`
  width: 100%;

  ${({ $isSticky }) =>
    $isSticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 20;
    `}
`;
